import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { ComboBox, IComboBoxControlProps } from '../ComboBox';
import { getMoment, getFormattedDateString } from 'utils/DateTimeUtils';
import { debounce } from 'debounce';
import { DropDownOption } from '../DropDownOption';
import { getMomentAndDateString } from 'components/inputs/date-time/helpers/DateTimePickerHelpers';
import { TimeFormats } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal/TimeFormats';
import { ValidationSummary } from 'utils/ValidationUtils';
import './TimePickerDropdown.less';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav';

interface ITimePickerDropdownProps extends TimePickerFormatOptions {
  /** Either a valid string, or else a moment object must be provided */
  currentDate?: string | moment.Moment;
  /** True if it's a a picker for both date and time */
  onChange: (date: moment.Moment) => void;
  /** Pass clear handling to handle clear, but also to show X */
  onClear?: () => void;
  /** Interval in minutes */
  interval?: number;
  validation?: ValidationSummary;
  className?: string;
  inputClass?: string;
  iconClass?: string;
  iconButtonClass?: string;
  overlayClass?: string;
  fontStyle?: React.CSSProperties;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  onExit?: (event?: React.FocusEvent<{}>) => void;
  useRippleEffect?: boolean;
}

const getClassName = (className?: string) => classNames('dpc-time-picker', className);
const getIconClassName = (isActive?: boolean, iconClass?: string) =>
  classNames('dpc-time-picker__icon', iconClass, {
    'dpc-time-picker__icon--active': isActive,
  });

const DEFAULT_INTERVAL = 15; // default to 15 minute intervals

/**
 * Time picker dropdown
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TimePickerDropdown(props: ITimePickerDropdownProps) {
  const {
    currentDate,
    onChange,
    onExit,
    interval,
    className,
    inputClass,
    iconClass,
    iconButtonClass,
    overlayClass,
    fontStyle,
    inputRef,
    useRippleEffect,
  } = props;
  const momentFormat = getMomentFormat(props);
  const [isOpen, setIsOpen] = useState(false);
  const { parsedMoment, parsedDateString } = getMomentAndDateString(momentFormat, currentDate);
  const [dateTimeString, setDateTimeString] = useState(parsedDateString);
  const [dateTime, setDateTime] = useState(parsedMoment);
  const [suggestion, setSuggestion] = useState<string>();
  const options = useMemo(() => getOptions(momentFormat, interval || DEFAULT_INTERVAL), [interval, momentFormat]);
  const [focusIndex, setFocusIndex] = useState(-1);

  const onChosen = useCallback(
    (value: string) => {
      const date = getMoment()(value, momentFormat);
      setDateTime(date);
      setDateTimeString(value);
      setIsOpen(false);
      setSuggestion(undefined);
      onChange(date);
    },
    // we don't want this callback to change if the onChange handler is a new pointer,
    // forcing everyone else to use useCallback for onChange for debounce to work reliably.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [momentFormat]
  );

  const validate = useCallback(
    async (dateText: string, currentSuggestion?: string) => {
      dateText = dateText.toUpperCase(); // case insensitive;
      const exact =
        dateText === currentSuggestion?.toUpperCase()
          ? currentSuggestion
          : options.find(option => option.toUpperCase() === dateText);
      if (exact) {
        onChosen(exact);
        return;
      }

      const valSuggestion = parseApproximateTime(dateText, momentFormat, props.showSeconds);
      // check again for exact match of new suggestion
      if (dateText === valSuggestion) {
        onChosen(valSuggestion);
        return;
      }
      setSuggestion(valSuggestion);
    },

    [momentFormat, onChosen, options, props.showSeconds]
  );

  const debouncedDateTextCheck = useMemo(() => debounce(validate, 200), [validate]);

  useEffect(() => {
    setDateTimeString(parsedDateString);
    setDateTime(parsedMoment);
    // we want this to run only if the string value coming in is different:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedDateString]);

  const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTimeString(e.target.value);
    debouncedDateTextCheck.clear();
    debouncedDateTextCheck(e.target.value, suggestion);
  };

  const acceptSuggestion = () => {
    if (!suggestion) {
      return;
    }
    if (suggestion) {
      // save value
      const momentValue = getMoment()(suggestion, momentFormat);
      setDateTimeString(momentValue.format(momentFormat));
      onChange(momentValue);
    } else {
      // put it back
      setDateTimeString(getFormattedDateString(dateTime, momentFormat));
    }
    setSuggestion(undefined);
    setIsOpen(false);
  };

  const choseOption = (id: string, value: string) => onChosen(value);

  const { keyboardNavHandler } = useListKeyboardNav(focusIndex, options, setFocusIndex, onChosen);
  const keyHandler = (e: React.KeyboardEvent) => {
    if (!suggestion) {
      return keyboardNavHandler(e);
    }
    e.preventDefault();
    switch (e.key) {
      case 'Enter':
        acceptSuggestion();
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const comboControl: IComboBoxControlProps = {
    open: isOpen || !!suggestion,
    onOpenClick: () => {
      setSuggestion(undefined);
      setIsOpen(true);
    },
    onClose: () => {
      setSuggestion(undefined);
      setIsOpen(false);
      setFocusIndex(-1);
    },
  };

  const onBlur = () => {
    acceptSuggestion();
    if (onExit) onExit();
    // we _could_ try to parse time out even with no suggestion,
    // but we can't actually get there when "now" is shown as a default all the time.
  };

  return (
    <ComboBox
      onChange={onFieldChange}
      className={getClassName(className)}
      openIconClass={getIconClassName(isOpen || !!suggestion, iconClass)}
      value={dateTimeString}
      placeholder={momentFormat}
      controlProps={comboControl}
      hideClear={true}
      onKeyUp={keyHandler}
      onBlur={onBlur}
      inputClass={inputClass}
      iconButtonClass={iconButtonClass || ''}
      overlayClass={overlayClass}
      fontStyle={fontStyle}
      inputRef={inputRef}
      useRippleEffect={useRippleEffect}
    >
      {suggestion && (
        <DropDownOption index={0} isFocused={true} onChosen={acceptSuggestion}>
          {suggestion}
        </DropDownOption>
      )}
      {!suggestion &&
        options.map((option, i) => (
          <DropDownOption
            key={i}
            index={i}
            id={option}
            onChosen={choseOption}
            value={option}
            isChosen={option === dateTimeString}
          >
            {option}
          </DropDownOption>
        ))}
    </ComboBox>
  );
}

export interface TimePickerFormatOptions {
  format?: string;
  showSeconds?: boolean;
  timeFormat: TimeFormats;
}

function getMomentFormat({ format, showSeconds, timeFormat }: TimePickerFormatOptions) {
  format = 'h:mm';
  if (showSeconds) {
    format = format.concat(':ss');
  }
  if (timeFormat === TimeFormats.Use12HourFormat) {
    format = format.concat(' A');
  }
  return format;
}

// not sure we need this...
// function getFormat({ format, showSeconds, timeFormat }: TimePickerFormatOptions) {
//   if (format) {
//     return format;
//   } else {
//     format = 'h:i';
//     if (showSeconds) {
//       format += ':s';
//     }
//     if (timeFormat === TimeFormats.Use12HourFormat) {
//       format += ' A';
//     } else {
//       format = format.replace('h', 'H');
//     }
//   }
//   return format;
// }

function getOptions(format: string, interval: number) {
  // Christmas Eve as arbitrary dummy date for collecting these
  const time = getMoment()('12/24/2000 12:00 AM');
  const items: string[] = [];

  while (time.date() < 25) {
    // stop when it rolls over to Christmas
    items.push(time.format(format));
    time.add(interval, 'minutes');
  }
  return items;
}

// this logic is lifted from MVC.Host/../timepicker.ts, because git changes there only a year old, so whatever fixes are there are relatively recent
function parseApproximateTime(currentTime: string, format: string, showSeconds = false) {
  let approximateTime: string = getMoment()().format(format);
  let hour: string | number | null,
    min: string,
    sec: string,
    amPM: string = 'AM';
  let parseTime: RegExp = /(1[0-9]{1}|2[0-3]{1}|0?[1-9]?)\D?[0-5]?[0-9]?\D?[0-5]?[0-9]?/; // for any splitter
  let parseTimeAlphaNumeric: RegExp = /(0{1}[1-9]{0,1}|1[0-2]{1})\D?([0-5]?[0-9]?)\D?[0-5]?[0-9]?/g; //for splitter in alpha numeric
  let parseTimeSection: RegExp = /(AM|PM)/i;
  let parseStart: RegExp = /^[a-z]+/i;

  if (parseTimeSection.test(currentTime)) {
    amPM = currentTime.match(parseTimeSection)![0];
  }

  //if val starts with char
  if (parseStart.test(currentTime)) {
    // time starts with characters.
    if (parseTimeAlphaNumeric.test(currentTime)) {
      let result = currentTime.match(parseTimeAlphaNumeric)![0].replace(/\D/g, '');
      hour = parseHour(parseInt(result.substr(0, 2)));
      if (hour !== null) {
        amPM = getValidSection(parseInt(result.substr(0, 2)), amPM);
        result = result.substr(2);
      } else {
        hour = parseHour(parseInt(result.substr(0, 1)));
        result = result.substr(1);
      }
      if (result === '') {
        min = '00';
        sec = '00';
      } else {
        if (isValidMinSec(parseInt(result.substr(0, 2)))) {
          min = result.substr(0, 2);
          min = min.length === 1 ? '0' + min : min;
          result = result.substr(2);
        } else {
          min = result.substr(0, 1);
          min = min.length === 1 ? '0' + min : min;
          result = result.substr(1);
        }

        if (result === '') {
          sec = '00';
        }

        if (isValidMinSec(parseInt(result))) {
          sec = result;
          sec = sec.length === 1 ? '0' + sec : sec;
        } else {
          sec = '00';
        }
      }
      return getTimeString(hour, min, sec, amPM, showSeconds);
    }
  }

  if (parseTime.test(currentTime)) {
    // any splitter
    var result = currentTime.match(parseTime)![0].replace(/\D/g, '');
    if (result) {
      hour = parseHour(parseInt(result.substr(0, 2)));
      if (hour != null) {
        amPM = getValidSection(parseInt(result.substr(0, 2)), amPM);
        result = result.substr(2);
      } else {
        hour = parseHour(parseInt(result.substr(0, 1)));
        result = result.substr(1);
      }

      if (result === '') {
        min = '00';
        sec = '00';
      } else {
        if (isValidMinSec(parseInt(result.substr(0, 2)))) {
          min = result.substr(0, 2);
          min = min.length === 1 ? '0' + min : min;
          result = result.substr(2);
        } else {
          min = result.substr(0, 1);
          min = min.length === 1 ? '0' + min : min;
          result = result.substr(1);
        }

        if (result === '') {
          sec = '00';
        }

        if (isValidMinSec(parseInt(result))) {
          sec = result;
          sec = sec.length === 1 ? '0' + sec : sec;
        } else {
          sec = '00';
        }
      }
      return getTimeString(hour, min, sec, amPM, showSeconds);
    }
  }
  return approximateTime;
}

function getValidSection(input: number, amPM: string) {
  if (input > 12 && input <= 23) {
    return 'PM';
  }
  return amPM;
}

// checks for valid hours.
function parseHour(input: number) {
  if (input === 0) {
    return '12';
  } else if (input > 0 && input <= 12) {
    return input;
  } else if (input > 12 && input <= 23) {
    return (input - 12).toString();
  }
  return null;
}

// check if input is valid seconds or minutes.
function isValidMinSec(input: number) {
  return input >= 0 && input <= 59 ? true : false;
}

function getTimeString(hour: string | number | null, min: string, sec: string, amPm: string, showSeconds = false) {
  const secString = showSeconds ? ':' + sec : '';
  return `${hour}:${min}${secString} ${amPm.toUpperCase()}`;
}
